import * as THREE from 'three';
import { blankFace, bronze, canDraw, type GlyphFace } from './hud-materials.js';
import { iron, polygonFrameGeometry, polygonGeometry } from './hud-parts.js';
import { ButtonKit } from './octagon-button.js';

/** A token's radius and the gap between tokens, in CSS pixels at full size; the rod reaches this far back toward the medallion. */
const TOKEN = 11, GAP = 4, ROD_REACH = 20;

/**
 * The orders a hero has queued, as a short row of round iron tokens strung on a bronze rod running out from its medallion, each with
 * the order's glyph cut into a slate face in pale steel: unlike the octagonal gilt buttons that gave the orders, so they read as
 * waiting, not pressable. The next order comes first, nearest the medallion; the later ones step back. It draws what it is given and
 * knows nothing of the rules.
 */
export class OrderQueueRow extends THREE.Group {
  private metal = bronze(17, 0x4f3d28);
  private rim = iron();
  private bezel = polygonFrameGeometry(24, TOKEN + 1.5, TOKEN - 1.5, 2, .6, 1);
  private plate = polygonGeometry(24, TOKEN - 1);
  private rod = new THREE.CylinderGeometry(1.1, 1.1, 1, 8).rotateZ(Math.PI / 2);
  private faces = new Map<string, { face: Promise<GlyphFace | null>; material: THREE.MeshStandardMaterial }>();
  private shown = '';
  private kit: ButtonKit;
  private ownsKit: boolean;

  /** `kit` shares the layer's drawn glyph faces, so a glyph several heroes have queued is drawn once; without it the row draws its own. */
  constructor(kit?: ButtonKit) {
    super();
    this.kit = kit ?? new ButtonKit(null); this.ownsKit = !kit;
  }

  /** The glyphs (icon URLs) of the queued orders, next first. */
  set glyphs(urls: string[]) {
    const key = urls.join('|');
    if (key === this.shown) return;
    this.shown = key; this.clear();
    if (!urls.length) return;
    const step = TOKEN * 2 + GAP, rod = new THREE.Mesh(this.rod, this.metal);
    // The rod runs from the medallion's rim to the last token, behind them, so the row hangs off the hero it belongs to.
    rod.scale.x = ROD_REACH + (urls.length - 1) * step; rod.position.set(rod.scale.x / 2 - ROD_REACH, 0, -3); this.add(rod);
    urls.forEach((url, index) => {
      const token = new THREE.Group(), face = new THREE.Mesh(this.plate, this.face(url));
      face.position.z = .4; token.add(new THREE.Mesh(this.bezel, index ? this.rim : this.metal), face);
      // The next order stands a touch larger, ringed in bronze; the rest are smaller, in iron.
      token.scale.setScalar(index ? .82 : 1);
      token.position.set(index * step, 0, index ? -1 : 0);
      this.add(token);
    });
  }
  /** Width of a row of `count` plaques, in CSS pixels at full size. */
  static width(count: number) { return count ? count * (TOKEN * 2 + GAP) - GAP : 0; }

  private face(url: string) {
    let entry = this.faces.get(url);
    if (!entry) {
      // Blank maps until the glyph is drawn, so the glyph arriving links no shader.
      const blank = blankFace(), material = new THREE.MeshStandardMaterial({ color: 0x24282b, metalness: .3, roughness: .6, map: blank.map, normalMap: blank.normalMap });
      // Cut into the face rather than raised, pale steel on slate: the buttons' glyphs are raised gilt on dark bronze.
      const face = canDraw() ? this.kit.glyph(url, 0x1e2326, 0xd0dade, false).then(texture => { if (texture) { material.map = texture.map; material.normalMap = texture.normalMap; material.color.setHex(0xffffff); } return texture; }) : Promise.resolve(null);
      entry = { face, material }; this.faces.set(url, entry);
    }
    return entry.material;
  }

  dispose() {
    this.clear(); this.bezel.dispose(); this.plate.dispose(); this.rod.dispose(); this.metal.dispose(); this.rim.dispose();
    for (const { material } of this.faces.values()) material.dispose();
    this.faces.clear();
    if (this.ownsKit) this.kit.dispose();
  }
}
